import type { CellClassParams, GridApi, IRowNode } from 'ag-grid-community';
import { entries, get, range } from 'lodash-es';
import type { DefaultTheme } from 'styled-components';
import type { ToastProps } from '../../hooks';
import { ModeEnum } from '../../types';
import type { Leaves } from '../../utils';
import { format, logger } from '../../utils';
import {
  DEFAULT_BULK_EDIT_NUM_ATTEMPTS,
  DEFAULT_BULK_EDIT_NUM_WORKERS,
  DEFAULT_BULK_EDIT_THROTTLE_MS,
  type ColumnDef,
  type GenericFilter,
} from '../BlotterTable';
import { Button, getButtonHorizontalSpacing, getIconSpacing, IconButton } from '../Button';
import type { FilterableProperty } from '../Filters';
import { FormControlSizes } from '../Form';
import { getFontSize } from '../Form/utils';
import { IconName } from '../Icons';
import { NotificationVariants } from '../Notification';
import { Tooltip } from '../Tooltip';
import { isNotDivider } from './components/InputsAndDropdownsDrawer/components/dividerUtils';
import { flattenDrawerOptions } from './components/InputsAndDropdownsDrawer/filterAndMapDrawerOptions';
import type { InputsAndDropdownsDrawerOptions } from './components/InputsAndDropdownsDrawer/types';
import type { EntityAdminClass } from './EntityAdminClass';
import type {
  AllowAddEntityConfig,
  AllowAddEntityResolved,
  EntityAdminModeSwitchConfiguration,
  EntityAdminRecord,
  HierarchicalColumnProps,
  RightPinnedButtonProps,
} from './types';

function resolveIsDisabledTooltip<T extends EntityAdminRecord>(
  isDisabledTooltip: RightPinnedButtonProps<T>['isDisabledTooltip'],
  entity: T
): string | undefined {
  return typeof isDisabledTooltip === 'function' ? isDisabledTooltip(entity) : isDisabledTooltip;
}

export function resolveAllowAddEntity(config: AllowAddEntityConfig | undefined): AllowAddEntityResolved {
  if (config === undefined) {
    return { main: false, child: false };
  }
  if (typeof config === 'boolean') {
    return { main: config, child: config };
  }
  return {
    main: config.main,
    child: config.child ?? false,
  };
}

/** True if any add is allowed (for path validation, drawer visibility) */
export function hasAnyAddAllowed(config: AllowAddEntityConfig | undefined): boolean {
  const { main, child } = resolveAllowAddEntity(config);
  return main || child;
}

const RIGHT_PINNED_ACTION_CELL_PADDING = 4;
const RIGHT_PINNED_ACTION_CELL_CLASS = 'ag-right-aligned-cell';
const RIGHT_PINNED_ACTION_CELL_STYLE = {
  paddingLeft: RIGHT_PINNED_ACTION_CELL_PADDING,
  paddingRight: RIGHT_PINNED_ACTION_CELL_PADDING,
  whiteSpace: 'nowrap' as const,
};
const ADD_CHILD_ENTITY_BUTTON_DEFAULT_TEXT = 'Add Child';
const RIGHT_PINNED_ICON_COLUMN_WIDTH = 40;
const LABELED_ACTION_GLYPH_EM = 0.6;

function getLabeledActionColumnWidth(label: string, theme: DefaultTheme): number {
  const fontSizePx = getFontSize(theme, FormControlSizes.Small) * theme.baseSize;
  const iconSizePx = theme.fontSizeDefault * theme.baseSize;
  return Math.ceil(
    RIGHT_PINNED_ACTION_CELL_PADDING * 2 +
      getButtonHorizontalSpacing(theme, FormControlSizes.Small) * 2 +
      theme.borderWidthButton * 2 +
      iconSizePx +
      getIconSpacing(FormControlSizes.Small) +
      label.length * fontSizePx * LABELED_ACTION_GLYPH_EM
  );
}

export function getAddChildEntityButtonText(childEntityName: string | undefined): string {
  return childEntityName ?? ADD_CHILD_ENTITY_BUTTON_DEFAULT_TEXT;
}

export const getEditColumn = <T extends EntityAdminRecord>({
  handleOnClick,
  isDisabled = () => false,
  isHidden = () => false,
  isDisabledTooltip,
  isPanelLayout = false,
  theme,
}: RightPinnedButtonProps<EntityAdminClass<T>> & {
  handleOnClick: (entityClass: EntityAdminClass<T>) => void;
  isPanelLayout?: boolean;
  theme: DefaultTheme;
}): ColumnDef<T> => {
  const width = isPanelLayout ? RIGHT_PINNED_ICON_COLUMN_WIDTH : getLabeledActionColumnWidth('Edit', theme);
  return {
    id: 'edit',
    type: 'custom',
    pinned: 'right',
    frozen: true,
    flex: 0,
    width,
    minWidth: width,
    maxWidth: width,
    cellClass: RIGHT_PINNED_ACTION_CELL_CLASS,
    cellStyle: RIGHT_PINNED_ACTION_CELL_STYLE,
    suppressHeaderMenuButton: true,
    suppressColumnsToolPanel: true,
    params: {
      cellRenderer: ({ node }: { node: IRowNode<EntityAdminClass<T>> }) => {
        if (node.data == null) {
          return null;
        }
        if (isHidden(node.data)) {
          return null;
        }
        const isNodeDisabled = isDisabled(node.data);
        const onClick = (event: { stopPropagation: () => void }) => {
          event.stopPropagation();
          const entityClass = node.data;
          node.setSelected(true, true);
          entityClass && handleOnClick(entityClass);
        };
        return (
          <Tooltip tooltip={isNodeDisabled ? resolveIsDisabledTooltip(isDisabledTooltip, node.data) : undefined}>
            {isPanelLayout ? (
              <IconButton
                icon={IconName.Pencil}
                size={FormControlSizes.Small}
                aria-label="Edit"
                onClick={onClick}
                disabled={isDisabled(node.data)}
              />
            ) : (
              <Button
                startIcon={IconName.Pencil}
                size={FormControlSizes.Small}
                onClick={onClick}
                disabled={isDisabled(node.data)}
              >
                Edit
              </Button>
            )}
          </Tooltip>
        );
      },
    },
  };
};
export const getDeleteColumn = <T extends EntityAdminRecord>({
  handleOnClickDelete,
  isDisabled = () => false,
  isHidden = () => false,
  isDisabledTooltip,
}: RightPinnedButtonProps<EntityAdminClass<T>> & {
  handleOnClickDelete: (entityClass: EntityAdminClass<T>) => void;
}): ColumnDef<T> => {
  const width = RIGHT_PINNED_ICON_COLUMN_WIDTH;
  return {
    id: 'delete',
    type: 'custom',
    pinned: 'right',
    frozen: true,
    flex: 0,
    width,
    minWidth: width,
    maxWidth: width,
    cellClass: RIGHT_PINNED_ACTION_CELL_CLASS,
    cellStyle: RIGHT_PINNED_ACTION_CELL_STYLE,
    suppressHeaderMenuButton: true,
    suppressColumnsToolPanel: true,
    params: {
      cellRenderer: ({ node }: { node: IRowNode<EntityAdminClass<T>> }) => {
        if (node.data == null) {
          return null;
        }
        if (isHidden(node.data)) {
          return null;
        }
        const isNodeDisabled = isDisabled(node.data);
        return (
          <Tooltip tooltip={isNodeDisabled ? resolveIsDisabledTooltip(isDisabledTooltip, node.data) : undefined}>
            <IconButton
              disabled={isNodeDisabled}
              icon={IconName.Trash}
              size={FormControlSizes.Small}
              aria-label="Delete"
              onClick={() => {
                const entityClass = node.data;
                entityClass && handleOnClickDelete(entityClass);
                node.setSelected(true, true);
              }}
            />
          </Tooltip>
        );
      },
    },
  };
};
export const getModeColumn = <T extends EntityAdminRecord>({
  handleOnSwitchMode,
  modeField,
  isDisabled = () => false,
  isHidden = () => false,
}: {
  handleOnSwitchMode: (entityClass: EntityAdminClass<T>, newMode: ModeEnum) => void;
  modeField: EntityAdminModeSwitchConfiguration<T>['modeField'];
  isDisabled?: EntityAdminModeSwitchConfiguration<T>['isDisabled'];
  isHidden?: EntityAdminModeSwitchConfiguration<T>['isHidden'];
}): ColumnDef<T> => ({
  id: 'mode',
  field: modeField,
  type: 'toggle',
  title: 'Mode',
  description: 'Enabled/Disabled',
  pinned: 'left',
  width: 72, // correct width to show "Mode" and not "Mo..."
  params: {
    valueSetter: params => {
      if (params.node == null) {
        return false;
      }
      const entityClass = params.node.data as EntityAdminClass<T>;
      const newMode = params.newValue ? ModeEnum.Enabled : ModeEnum.Disabled;
      entityClass && handleOnSwitchMode(entityClass, newMode);
      // false because in case of confirmation, we don't want to update the value just yet;
      // it'll get updated when the data changes
      return false;
    },
    isDisabled: (data: T | undefined) => data == null || isDisabled?.(data),
    isHidden: (data: T | undefined) => data == null || isHidden?.(data),
  },
});

export const getAddChildEntityColumn = <T extends EntityAdminRecord>({
  openEntityAdminDrawer,
  onAddChildEntity,
  childEntityName,
  theme,
}: HierarchicalColumnProps<T> & { childEntityName?: string; theme: DefaultTheme }): ColumnDef<T> => {
  const text = getAddChildEntityButtonText(childEntityName);
  const width = getLabeledActionColumnWidth(text, theme);
  return {
    id: 'add-child',
    type: 'button',
    pinned: 'right',
    frozen: true,
    flex: 0,
    width,
    minWidth: width,
    maxWidth: width,
    cellClass: RIGHT_PINNED_ACTION_CELL_CLASS,
    cellStyle: RIGHT_PINNED_ACTION_CELL_STYLE,
    suppressColumnsToolPanel: true,
    params: {
      disabled: ({ node }: { node: IRowNode<EntityAdminClass<T>> }) => node.data?.entityIDField == null,
      startIcon: IconName.Plus,
      onClick: ({ node, api }: { node: IRowNode<EntityAdminClass<T>>; api: GridApi<EntityAdminClass<T>> }) => {
        if (node.data == null) {
          return;
        }
        if (onAddChildEntity) {
          onAddChildEntity({ node, api, openEntityAdminDrawer });
        } else {
          const setupEntity = node.data.clone();
          openEntityAdminDrawer({ setupEntities: [setupEntity], addingChildEntity: true });
        }
        api.deselectAll();
      },
      hide: (data: T) => get(data, data.childIDField) != null,
      children: text,
      size: FormControlSizes.Small,
    },
  };
};

export const applyInheritanceCellStyle = <T extends EntityAdminRecord>(column: ColumnDef<T>): ColumnDef<T> => ({
  ...column,
  cellStyle: (params: CellClassParams<EntityAdminClass<T>>) => ({
    ...(typeof column.cellStyle === 'function' ? column.cellStyle(params) : column.cellStyle),
    // If this column field is inherited, make it slightly transparent. We overwrite any existing cellStyle opacity.
    opacity: params.data?.isFieldInherited(params.colDef.field as keyof T) ? 0.5 : 1,
  }),
});

export const onlyServerFilterEntries = (
  filter: GenericFilter,
  filterableProperties: FilterableProperty[]
): GenericFilter => {
  return entries(filter).reduce((acc, [key, values]) => {
    const foundProperty = filterableProperties.find(property => property.key === key);
    if (foundProperty == null || foundProperty.control === 'text') {
      return acc;
    }

    const selections = Array.isArray(values) ? values : values != null ? [values] : [];
    if (selections.length === 0) {
      return acc;
    }

    acc[key] = foundProperty.control === 'single-select' ? selections.at(0)! : selections;
    return acc;
  }, {} as GenericFilter);
};

export async function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Process a set of entities in parallel with retry‐on‐fail logic.
 */
export async function bulkEditQueue<T>(
  entities: T[],
  newEntity: EntityAdminRecord,
  updateFn: (
    entity: T,
    newEntity: EntityAdminRecord,
    showSuccess?: boolean,
    showError?: boolean
  ) => Promise<void | EntityAdminRecord | undefined>,
  addToast: (props: ToastProps) => void,
  removeToast: (id: string) => void
): Promise<{ success: number; failure: number }> {
  const total = entities.length;
  const toastID = 'bulk-edit-toast';

  addToast({
    id: toastID,
    text: `Bulk editing ${format(total)} entities…`,
    variant: NotificationVariants.Attention,
    timeout: null,
    dismissable: false,
  });

  let success = 0;
  let failure = 0;
  const queue = entities.slice();

  async function attemptUpdate(entity: T): Promise<boolean> {
    const maxAttempts = DEFAULT_BULK_EDIT_NUM_ATTEMPTS;
    let attempt = 1;

    while (attempt <= maxAttempts) {
      try {
        // never display success here, and only display an error from the final attempt.
        await updateFn(entity, newEntity, false, attempt === maxAttempts);
        return true;
      } catch {
        if (attempt === maxAttempts) {
          logger.warn(`Could not update entity after ${attempt} attempts.`, { entity });
          return false;
        }
        // throttle before next retry
        await delay(DEFAULT_BULK_EDIT_THROTTLE_MS * attempt);
        attempt++;
      }
    }

    return false;
  }

  async function worker() {
    while (queue.length) {
      const item = queue.shift()!;
      const ok = await attemptUpdate(item);
      ok ? success++ : failure++;

      removeToast(toastID);
      addToast({
        id: toastID,
        text: `Applied ${format(success)}/${format(total)}…`,
        variant: NotificationVariants.Attention,
        timeout: null,
        dismissable: false,
      });
    }
  }

  await Promise.all(range(DEFAULT_BULK_EDIT_NUM_WORKERS).map(() => worker()));

  removeToast(toastID);
  if (success === total) {
    addToast({ text: `All changes applied to ${format(success)} entities.`, variant: NotificationVariants.Positive });
  } else if (success > 0) {
    addToast({
      text: `Partially applied changes: ${format(success)} succeeded and ${format(failure)} failed.`,
      variant: NotificationVariants.Warning,
    });
  } else {
    addToast({
      text: `No changes were applied to ${format(failure)} entities.`,
      variant: NotificationVariants.Negative,
    });
  }

  return { success, failure };
}

/**
 * Extracts field names from drawer options, filtering out divider options that don't have fields.
 */
export function extractFieldsFromDrawerOptions<TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>
): (keyof TDrawerRecord)[] {
  return flattenDrawerOptions(drawerOptions)
    .filter(isNotDivider)
    .map(option => option.field);
}

export function removeHiddenFields<TDrawerRecord extends EntityAdminRecord>(
  entity: TDrawerRecord,
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>
): TDrawerRecord {
  // Remove all Drawer Hidden fields from the entity to PATCH/POST.
  const entityCopy = { ...entity };
  flattenDrawerOptions(drawerOptions)
    .filter(isNotDivider)
    // This specific case for entityCopy === originalEntity is ok because we haven't stripped values from the entity yet
    // and some isHidden handlers look at the original entity for full context
    .filter(option => option.getIsHidden?.(entityCopy, entityCopy))
    .forEach(({ field }) => {
      delete entityCopy[field];
    });

  return entityCopy;
}

/**
 * Determines the parent entity from selected entities when adding a child entity.
 */
function getParentEntity<TRecord extends EntityAdminRecord>(
  addingChildEntity: boolean,
  selectedEntities: EntityAdminClass<TRecord>[]
): TRecord | undefined {
  const firstSelectedEntity = selectedEntities.at(0);
  if (!firstSelectedEntity) {
    return undefined;
  }
  return addingChildEntity ? firstSelectedEntity.data : firstSelectedEntity.parentEntity;
}

/**
 * Creates the save context object for EntityAdmin drawer save operations.
 */
export function createSaveContext<TRecord extends EntityAdminRecord>(params: {
  isEditing: boolean;
  addingChildEntity: boolean;
  selectedEntities: EntityAdminClass<TRecord>[];
}): { isCreate: boolean; parentEntity?: TRecord } {
  const { isEditing, addingChildEntity, selectedEntities } = params;
  return {
    isCreate: !isEditing,
    parentEntity: getParentEntity(addingChildEntity, selectedEntities),
  };
}

/**
 * Constructs the row ID for an entity, matching the logic in EntityAdminClass[ENTITY_INTERNAL_ROW_ID].
 * For parent rows: `${entityID}`. For child rows: `${entityID}-${childID}`.
 */
export function getEntityRowId<TRecord extends EntityAdminRecord>(
  entity: TRecord,
  entityIDField: Leaves<TRecord>,
  childIDField?: Leaves<TRecord>
): string {
  const entityID = get(entity, entityIDField);
  const childEntityID = childIDField ? get(entity, childIDField) : undefined;
  return childEntityID ? `${entityID}-${childEntityID}` : `${entityID}`;
}
